/**
 * Get Chart Point Positions Tool - Schema Definitions
 *
 * Zod schemas and response types for querying specific chart point positions.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// INPUT SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

export const ChartPointQuerySchema = z.object({
    shapeName: z.string()
        .optional()
        .describe('Chart shape name to query (e.g., "Chart 1"). If omitted, all charts are searched.'),
    seriesIndex: z.number()
        .int()
        .default(0)
        .describe("Series index to query (0-based)."),
    xValues: z.array(z.union([z.string(), z.number()]))
        .min(1)
        .describe("Category names, date strings, or 0-based point indices to locate."),
});

export const GetChartPointPositionsParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    slideIndex: z.number()
        .int()
        .positive()
        .describe("One-based index of the slide in the presentation."),
    queries: z.array(ChartPointQuerySchema)
        .min(1, "At least one chart point query is required.")
        .describe("Chart point queries to resolve to positions."),
});

// ═══════════════════════════════════════════════════════════════════════════
// OUTPUT TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ChartPointAnchor {
    x: number;
    y: number;
}

export interface ChartPointBounds {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface ChartPointEntry {
    i: number;
    cat?: string;
    val?: number;
    anchor: ChartPointAnchor;
    bounds?: ChartPointBounds;
}

export interface ChartPointSeries {
    si: number;
    name?: string;
    pts: ChartPointEntry[];
}

export interface ChartShapeInfo {
    shapeName?: string;
    shapeId?: string;
    position: ChartPointBounds;
}

export interface ChartPointPositionsResult {
    shapeName?: string;
    shapeId?: string;
    shapePosition: ChartPointAnchor;
    shapeBounds?: ChartPointBounds;
    series: ChartPointSeries[];
}

export interface GetChartPointPositionsResponse {
    slideIndex: number;
    results: ChartPointPositionsResult[];
    chartShapes: ChartShapeInfo[];
    summary: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type ChartPointQuery = z.infer<typeof ChartPointQuerySchema>;
export type GetChartPointPositionsParams = z.infer<typeof GetChartPointPositionsParamsSchema>;
